import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef, ViewContainerRef, AfterViewInit } from '@angular/core';
import { LightShowOperation } from '../Operations/lightshow-operation';
import { Subject, Subscription } from 'rxjs';
import { LightShowService } from '../../../app/_services/light-show.service';
import { takeUntil } from 'rxjs/operators';
import { TorchOperation } from '../Operations/torch-operation';
import { StrobeOperation } from '../Operations/strobe-operation';
import { SparkleOperation } from '../Operations/sparkle-operation';
import { LightshowState } from '../lightshow-state';
import { ErrorTooltipComponent } from '../Components/error-tooltip.component';
import { FormGroup } from '@angular/forms';
import { LightShowTrack } from '../../_models/LightShowTrack';
import * as moment from 'moment';
import { DialogService } from '../../../app/_services/dialog.service';
import { FlashTypes } from '../operation-types';
import { SequenceOperation } from '../Operations/sequence-operation';

@Component({
  selector: 'app-lightshow-torch-editor',
  templateUrl: './lightshow-torch-editor.component.html',
  styleUrls: [
    './lightshow-torch-editor.component.css',
    '../lightshow-preview.component.css'
  ]
})
export class LightshowTorchEditorComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('timeline', {static: false})
  timeline: ElementRef;
  @ViewChild('start', {static: false})
  start: ElementRef;
  @ViewChild('duration', {static: false})
  duration: ElementRef;

  private _ngDestroy$ = new Subject<void>();
  private _playPositionChanges = new Subject<number>();
  private _playPosition: number;
  private get MINIMUM_TORCH_DURATION() {
    return this.lightshowService.MINIMUM_TORCH_DURATION;
  }

  get playPosition() {
    return this._playPosition;
  }

  @Input()
  lightshowOperations: LightShowOperation[];
  @Input()
  set playPosition(value: number) {
    this._playPosition = value;
    this._playPositionChanges.next(value);
  }
  @Input()
  player: HTMLDivElement;
  @Input()
  totalDuration: number;
  @Input()
  main: HTMLDivElement;
  @Input()
  state: LightshowState;
  @Input()
  timelineError: ErrorTooltipComponent;
  @Input()
  track: LightShowTrack;
  @Input()
  form: FormGroup;
  @Input()
  disableHoverTips = false;

  get selectedOperation() {
    const uniqueId = this.lightshowService.selectedOperations[0];
    return this.lightshowOperations.find(op => op.uniqueId === uniqueId) || this.lightshowOperations[0];
  }

  set selectedOperation(operation: LightShowOperation) {
    const uniqueId = this.lightshowService.selectedOperations[0];
    const targetOperation = this.lightshowOperations.find(op => op.uniqueId === uniqueId);
    const targetIndex = this.lightshowOperations.indexOf(targetOperation);

    if (targetIndex !== -1) {
      this.lightshowOperations[targetIndex] = operation;
    }
  }

  selectedOperationIndex = 0;
  selectedOperationType = 'SOLID';

  playPosition$: Subscription;
  showInstructions: false;
  flashTypes = ['SOLID', 'STROBE', 'SPARKLE'];

  mainKeyDown: (e: KeyboardEvent) => void;
  mainKeyUp: (e: KeyboardEvent) => void;
  playerKeyDown: (e: KeyboardEvent) => void;
  playerKeyUp: (e: KeyboardEvent) => void;
  documentMouseclick: (mouseEv: MouseEvent) => void;
  hotKeyDown: (e: KeyboardEvent) => void;
  navigateTorchesKeyDown: (e: KeyboardEvent) => void;

  shouldFreeze: boolean;

  startFocus: boolean = false;
  durationFocus: boolean = false;

  constructor(private lightshowService: LightShowService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef) { }

  ngOnInit() {


    this.lightshowService.controlPlusAPressed$
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(_ => {
        for (const op of this.lightshowOperations) {
          if (!this.lightshowService.checkIfOperationIsSelected(op.uniqueId)) {
            this.lightshowService.selectOperation(op.uniqueId);
          }
        }
      });

    this.lightshowService.operationSelected$
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(uniqueId => this.selectOperation(uniqueId));

      this.lightshowService.operationDeselected$
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(uniqueId => this.deselectOperations());

    this.lightshowService.torchFreezer$
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(shouldFreeze => this.shouldFreeze = shouldFreeze);

  }

  ngAfterViewInit() {
    this._attachEventListeners();
  }

  getSelectedOperationsLength() {
    return this.lightshowService.selectedOperations ? this.lightshowService.selectedOperations.length : 0;
  }

  deselectOperations() {
    if (this.lightshowService.selectedOperations) {
      this.lightshowService.selectedOperations = [];
    }
    this.lightshowService.selectedOperationChange$.next();
  }

  scrollTimeline = () => {
    try {
      this.timeline.nativeElement.scrollLeft = this.timeline.nativeElement.scrollWidth;
    } catch (error) {
      console.error(error);
    }
  }

  addSelectedOperation() {
    let newOperation: LightShowOperation;

    switch (this.selectedOperationType) {
      case 'STROBE':
        newOperation = new StrobeOperation(this.playPosition, this.MINIMUM_TORCH_DURATION, []);
        break;
      case 'SOLID':
        newOperation = new TorchOperation(this.playPosition, this.MINIMUM_TORCH_DURATION, []);
        break;
      case 'SPARKLE':
        newOperation = new SparkleOperation(FlashTypes.sparkleSteady, this.playPosition, this.MINIMUM_TORCH_DURATION, []);
        break;
      case 'SEQUENCE':
        newOperation = new SequenceOperation(this.playPosition, this.MINIMUM_TORCH_DURATION);
      break;
    }

    this.checkOperation(newOperation);
  }

  checkOperation(newOperation: LightShowOperation): boolean {
    this.form.markAsDirty();

    const incompatibleTorch = this._checkForIncompatibleOperation(newOperation);

    if (!incompatibleTorch && !this.lightshowOperations.find(op => op.position === newOperation.position)) {
      this._registerOperation(newOperation);

      const index = this.lightshowOperations.indexOf(newOperation);

      if (index !== -1 && this.lightshowService.selectedOperations.length <= 1) {
        this.selectOperation(this.lightshowOperations[index].uniqueId);
      }

      this.lightshowService.operationArrayChange$.next();
    } else if (this.lightshowOperations.length) {
      const trackDurationMs = moment.duration(this.track.duration).asMilliseconds();

      const cutOff = trackDurationMs - (this.form.get('cropEndPercent').value / 100) * trackDurationMs;

      const availableSpace = [];

      const sortedSegments = this.lightshowOperations.sort((a, b) => a.position - b.position);

      for (let i = 0; i < sortedSegments.length - 1; i++) {
        if (sortedSegments[i + 1].position - (sortedSegments[i].position + sortedSegments[i].duration) >= this.MINIMUM_TORCH_DURATION) {
          availableSpace.push(sortedSegments[i].position + sortedSegments[i].duration);
        }
      }

      if (sortedSegments[0].position >= this.MINIMUM_TORCH_DURATION) {
        availableSpace.unshift(0);
      }

      if (cutOff - (sortedSegments[sortedSegments.length - 1].position + sortedSegments[sortedSegments.length - 1].duration) >= this.MINIMUM_TORCH_DURATION) {
        availableSpace.push(sortedSegments[sortedSegments.length - 1].position + sortedSegments[sortedSegments.length - 1].duration);
      }

      if (!availableSpace.length || incompatibleTorch) {
        this.timelineError.showAndHide('No space to add a torch');
        return false;
      } else {
        let index = 0;

        availableSpace.forEach((num, i) => {
          if (cutOff - num < cutOff - availableSpace[index]) {
            index = i;
          }
        });

        const op = new TorchOperation(availableSpace[index], this.MINIMUM_TORCH_DURATION, []);
        this._registerOperation(op);

        if (this.lightshowService.selectedOperations.length <= 1) {
          this.selectOperation(op.uniqueId);
        }

        this.lightshowService.operationArrayChange$.next();
      }
    }

    setTimeout(() => this.scrollTimeline(), 0);
    return true;
  }

  selectOperation(uniqueId: string) {
    this.form.markAsDirty();
    this.lightshowService.selectOperation(uniqueId);

    if (uniqueId) {
      this._setModel(this.selectedOperation.flashType);
      this.scrollTimelineToOperation(uniqueId);
    }
  }

  deselectOperation(uniqueId: string) {
    this.form.markAsDirty();
    this.lightshowService.deselectOperation(uniqueId);
  }

  scrollTimelineToOperation(uniqueId: string) {
    const operation = this.lightshowOperations.find(op => op.uniqueId === uniqueId);

    const index = this.lightshowOperations.indexOf(operation);

    const clientWidth = this.timeline.nativeElement.clientWidth;

    const percentage = (index * 100) / this.lightshowOperations.length;

    this.timeline.nativeElement.scrollLeft = Math.max(0, (percentage * this.timeline.nativeElement.scrollWidth / 100) - (clientWidth / 2));
  }

  checkIfOperationIsSelected(uniqueId: string) {
    return this.lightshowService.checkIfOperationIsSelected(uniqueId);
  }

  deleteOperation(indexOfOperation: number) {
    if (!this.lightshowService.isCtrlPressed) { // So you can't select and delete operation at the same time
      this.lightshowOperations.splice(indexOfOperation, 1);

      this.lightshowService.operationArrayChange$.next();

      let index = indexOfOperation;

      if (this.lightshowOperations.length) {
        if (this.lightshowService.selectedOperations.length <= 1) {
          index = Math.max(index - 1, 0);
          this.selectOperation(this.lightshowOperations[index].uniqueId);
        }
      } else {
        this.selectOperation(null);
      }
    }
  }

  hideOperation(indexOfOperation: number) {
    if (!this.lightshowService.isCtrlPressed) { // So you can't select and delete operation at the same time
      this.lightshowOperations[indexOfOperation].show = !this.lightshowOperations[indexOfOperation].show;
    }
  }

  setSelectedOperationPosition(position: number) {
    this.form.markAsDirty();
    const originalPosition = this.selectedOperation.position;
    const wantedEnd = position + this.selectedOperation.duration;
    const trackDurationMs = moment.duration(this.track.duration).asMilliseconds();
    const cutOff = trackDurationMs - (this.form.get('cropEndPercent').value / 100) * trackDurationMs;

    if (position > 0) {
      if (this.lightshowOperations.length > 1) {
        const incompatibleElement = this.lightshowOperations.find(op => {
          const c1 = this.selectedOperation.uniqueId !== op.uniqueId;
          const c2 = position > op.position && position < op.position + op.duration;
          const c3 = wantedEnd > op.position && wantedEnd <= op.position + op.duration;
          const c4 = position < op.position && wantedEnd > op.position + op.duration;
          return (this.selectedOperation.uniqueId !== op.uniqueId &&
          this.selectedOperation.flashType !== op.flashType && (
          (position > op.position && position < op.position + op.duration) ||
          (wantedEnd > op.position && wantedEnd <= op.position + op.duration) ||
          (position < op.position && wantedEnd > op.position + op.duration))
        ); });

        if (incompatibleElement) {
          this.selectedOperation.position = originalPosition;
          this.start.nativeElement.value = (originalPosition / 1000).toFixed(2);
          this.timelineError.showAndHide('Torches of the same type are not allowed to overlap each other');
        } else if (wantedEnd > cutOff) {
          this.selectedOperation.position = originalPosition;
          this.start.nativeElement.value = (originalPosition / 1000).toFixed(2);

          this.timelineError.showAndHide('Torches can\'t be moved past the end of the track');
        } else {
          this.selectedOperation.position = position;
          this.lightshowService.operationArrayChange$.next();
        }
      } else {
        this.selectedOperation.position = position;
        this.lightshowService.operationArrayChange$.next();
      }
    } else {
      this.timelineError.showAndHide('Torch position can\'t be a negative number or move past the end of the track ');

      this.selectedOperation.position = originalPosition;
      this.start.nativeElement.value = (originalPosition / 1000).toFixed(2);
    }
  }

  setSelectedOperationDuration(duration: number) {
    const initialDuration = this.selectedOperation.duration;
    const end = Math.abs(this.selectedOperation.position + duration);
    const trackDurationMs = moment.duration(this.track.duration).asMilliseconds();
    const cutOff = trackDurationMs - (this.form.get('cropEndPercent').value / 100) * trackDurationMs;

    if (end < 0) {
      this.selectedOperation.duration = initialDuration;
      this.duration.nativeElement.value = ((this.selectedOperation.position + initialDuration) / 1000).toFixed(2);

      this.timelineError.showAndHide('Torch can\'t end at a negative time');
    } else if (duration < this.MINIMUM_TORCH_DURATION) {
      this.selectedOperation.duration = this.MINIMUM_TORCH_DURATION;
      this.duration.nativeElement.value = (this.MINIMUM_TORCH_DURATION / 1000).toFixed(2);

      this.timelineError.showAndHide(`Torch end is below minimum allowed value of ${this.MINIMUM_TORCH_DURATION * 0.001}s`);
    } else if (end < this.selectedOperation.position) {
      this.selectedOperation.duration = initialDuration;
      this.duration.nativeElement.value = (initialDuration / 1000).toFixed(2);

      this.timelineError.showAndHide('Torch can not end before it starts!');
    } else if (end > cutOff) {
      this.selectedOperation.duration = initialDuration;
      this.duration.nativeElement.value = (initialDuration / 1000).toFixed(2);

      this.timelineError.showAndHide('Torch can\'t end after the track');
    } else {
      const incompatibleElement = this.lightshowOperations.find(op =>
        ((end > op.position
          && end < op.position + op.duration)
          || (this.selectedOperation.position < op.position
            && end > op.position + op.duration))
        && op.uniqueId !== this.selectedOperation.uniqueId);

      if (incompatibleElement) {
        this.duration.nativeElement.value = (this.selectedOperation.duration / 1000).toFixed(2);

        this.timelineError.showAndHide('Torch will overlap with another torch');
      } else {
        this.selectedOperation.duration = duration;
        this.lightshowService.operationArrayChange$.next();
      }
    }
  }

  changeSelectedOperationsType(type: string | 'SOLID' | 'STROBE' | 'SPARKLE', arrayOfIds = this.lightshowService.selectedOperations) {
    if (arrayOfIds.length) {
      this.lightshowOperations.forEach((op, index) => {
        if (arrayOfIds.includes(op.uniqueId)) {
          if (op.flashType !== this.flashTypes[type]) {
            this.lightshowOperations[index] = this.operationTypeHandler(type, op);
          }
        }
      });

      this.lightshowService.operationArrayChange$.next();
    }
  }

  operationTypeHandler(type: string | 'SOLID' | 'STROBE' | 'SPARKLE', operation: LightShowOperation): LightShowOperation {
    let newOperation: LightShowOperation;

    switch (type) {
      case 'SOLID': {
        newOperation = new TorchOperation(
          operation.position,
          operation.duration,
          operation.sectionList,
          false,
          operation.uniqueId
        );
        operation = newOperation;
        break;
      }
      case 'STROBE': {
        newOperation = new StrobeOperation(
          operation.position,
          operation.duration,
          operation.sectionList,
          false,
          operation.uniqueId
        );
        operation = newOperation;
        break;
      }
      case 'SPARKLE': {
        this._showSparkleAlert();

        newOperation = new SparkleOperation(
          FlashTypes.sparkleSteady,
          operation.position,
          operation.duration,
          operation.sectionList,
          false,
          operation.uniqueId
        );
        operation = newOperation;
        break;
      }
    }

    return newOperation;
  }

  preventAutoPlay(e: KeyboardEvent) {

    if (e.key === 'Enter') {

      e.preventDefault();
    }
  }

  private _showSparkleAlert() {
    if (!localStorage.getItem('sparkleAlertShowed')) {
      this.dialogService.alert(
        'Sparkle Flash Type Notes',
        'This flash causes the crowd\'s phone to blink randomly, rather than together. Think of statium full of bright camera flashes! This flash type is currently only supported by iOS devices' ,
        this.viewRef
      );

      localStorage.setItem('sparkleAlertShowed', 'true');
    }
  }

  private _checkForIncompatibleOperation(operation: LightShowOperation = this.selectedOperation): LightShowOperation | undefined {
    return this.lightshowOperations.find(op =>
      (operation.position > op.position && operation.position < op.position + op.duration) ||
      (operation.position + operation.duration > op.position && operation.position + operation.duration < op.position + op.duration) ||
      (operation.position < op.position && operation.position + operation.duration > op.position + op.duration) ||
      (operation.position > op.position && operation.position + operation.duration < op.position + op.duration)

    );
  }

  private _attachEventListeners() {
    this.main.addEventListener('keydown', this.mainKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
      }
    });

    this.main.addEventListener('keyup', this.mainKeyUp = (e: KeyboardEvent) => {

      if (!this.state.isPlaying) {
        return;
      }

      if (e.code === 'Space') {

        e.preventDefault();
      }
    });

    this.main.addEventListener('keydown', this.hotKeyDown = (e: KeyboardEvent) => {

      if (this.state.isPlaying) {
        return;
      }

      if (!this.state.isPlaying && this.selectedOperation && !(this.startFocus || this.durationFocus)){

        e.preventDefault();

        const operationTypes = ['torchOn', 'strobeOn', 'sparkleSteady'];
        let typeIndex = operationTypes.indexOf(FlashTypes[this.selectedOperation.flashType]);

        if (e.code === 'ArrowUp') {

          typeIndex++;

        } else if(e.code === 'ArrowDown') {

          typeIndex--;
        }

        typeIndex = ((typeIndex % 3) + 3) % 3;

        this.changeSelectedOperationsType(this.flashTypes[typeIndex]);
      }
    });

    this.main.addEventListener('keydown', this.navigateTorchesKeyDown = (e: KeyboardEvent) => {

      if (this.state.isPlaying) {

        return;
      }

      if (!this.state.isPlaying && this.selectedOperation && !(this.startFocus || this.durationFocus)) {

        e.preventDefault();

        const operationsLength = this.lightshowOperations.length;
        let opIndex = this.lightshowOperations.indexOf(this.selectedOperation);

        if (e.code === 'ArrowLeft' && opIndex > 0) {

          opIndex--;

        } else if (e.code === 'ArrowRight' && opIndex < operationsLength - 1) {

          opIndex++;
        }

        this.selectOperation(this.lightshowOperations[opIndex++].uniqueId)
      }
    });

    let inputQueue: string[] = [];

    this.player.addEventListener('keydown', this.playerKeyDown = (e: KeyboardEvent) => {

      if (!this.state.isPlaying) {
        return;
      }

      e.preventDefault();

      if (inputQueue.length && inputQueue[inputQueue.length - 1] === e.code){
        return;
      }

      if ((e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') && !e.repeat) {
        inputQueue.unshift(e.code);
        if (inputQueue.length > 1){
          const lastInput = inputQueue[inputQueue.length-1];
          const newKbEvent = new KeyboardEvent('keyup');
          Object.defineProperty(newKbEvent, 'code', {get: () => lastInput});
          this.playerKeyUp(newKbEvent);
        }

        this.lightshowService.torchFreezer$.next(true);

        const start = this.playPosition;

        let newOperation: LightShowOperation;

        if (e.code === 'Space'){
          switch (this.selectedOperationType) {
            case 'STROBE':
              newOperation = new StrobeOperation(start, this.MINIMUM_TORCH_DURATION, []);
              break;
            case 'SOLID':
              newOperation = new TorchOperation(start, this.MINIMUM_TORCH_DURATION, []);
              break;
            case 'SPARKLE':
              newOperation = new SparkleOperation(FlashTypes.sparkleSteady, start, this.MINIMUM_TORCH_DURATION, []);
              break;
          }
        } else {
          switch (e.code) {
            case 'ArrowLeft':
              newOperation = new StrobeOperation(start, this.MINIMUM_TORCH_DURATION, []);
              break;
            case 'ArrowUp':
              newOperation = new TorchOperation(start, this.MINIMUM_TORCH_DURATION, []);
              break;
            case 'ArrowRight':
              newOperation = new SparkleOperation(FlashTypes.sparkleSteady, start, this.MINIMUM_TORCH_DURATION, []);
              break;
          }
        }

        this._registerOperation(newOperation);

        const index = this.lightshowOperations.indexOf(newOperation);

        document.addEventListener('mousedown', this.documentMouseclick = (mouseEv: MouseEvent) => {
          mouseEv.preventDefault();
        });

        if (index !== -1) {
          this.selectOperation(this.lightshowOperations[index].uniqueId);
          const trackDurationMs = moment.duration(this.track.duration).asMilliseconds();

          const cutOff = trackDurationMs - (this.form.get('cropEndPercent').value / 100) * trackDurationMs;

          this.playPosition$ = this._playPositionChanges
            .subscribe(position => {
              this.state.isFlashOn = true;
              // Change the duration only when it's over the minimum operation duration
              if (position - start > this.MINIMUM_TORCH_DURATION) {
                if (this.lightshowOperations[index].position + this.lightshowOperations[index].duration < cutOff && position > 0) {
                  this.lightshowOperations[index].duration = position - start;
                } else {
                  this.lightshowOperations[index].duration = cutOff - this.lightshowOperations[index].position;
                }
              }
            });
        }
      }
    });

    this.player.addEventListener('keyup', this.playerKeyUp = (e: KeyboardEvent) => {

      if (!this.state.isPlaying) {
        return;
      }

      e.preventDefault();

      this.lightshowService.torchFreezer$.next(false);
      if (e.code === inputQueue[inputQueue.length - 1]){

        if ((e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') && this.playPosition$ && !this.playPosition$.closed) {
          this.form.markAsDirty();
          this.playPosition$.unsubscribe();
          this.state.isFlashOn = true;
          document.removeEventListener('mousedown', this.documentMouseclick);
          this.lightshowService.operationArrayChange$.next();
          setTimeout(() => this.scrollTimeline(), 0);

          const newOperation = this.selectedOperation;
          const arr = [];
          this.lightshowOperations.forEach(op => {
            if (newOperation.uniqueId !== op.uniqueId &&
              ((op.position > newOperation.position && op.position < newOperation.position + newOperation.duration)
                || (op.position + op.duration > newOperation.position
                  && op.position + op.duration < newOperation.position + newOperation.duration)
                || (newOperation.position < op.position + op.duration && newOperation.position > op.position))) {
              arr.push(this.lightshowOperations.indexOf(op));
            }
          });

          arr.sort((a, b) => b - a).forEach(x => this.lightshowOperations.splice(x, 1));
        }
        inputQueue.pop();
      }
    });
  }

  private _setModel(type: number) {
    switch (type) {
      case FlashTypes.torchOn:
        this.selectedOperationType = 'SOLID';
        break;
      case FlashTypes.strobeOn:
        this.selectedOperationType = 'STROBE';
        break;
      case FlashTypes.sparkleRampUp:
      case FlashTypes.sparkleSteady:
      case FlashTypes.sparkleRampDown:
        this.selectedOperationType = 'SPARKLE';
        break;
      case FlashTypes.sequenceStart:
        this.selectedOperationType = 'SEQUENCE';
        break;
    }
  }

  private _registerOperation(operation: LightShowOperation) {
      this.lightshowOperations.push(operation);

      // every time we add a new operation, we ensure that the timeline will order them by their position,
      // the same way they naturally appear on the mp3 player groove
      this.lightshowOperations.sort((a, b) => a.position - b.position);
  }

  ngOnDestroy() {
    this._ngDestroy$.next();
    this._ngDestroy$.complete();

    this.main.removeEventListener('keydown', this.mainKeyDown);
    this.main.removeEventListener('keyup', this.mainKeyUp);
    this.player.removeEventListener('keydown', this.playerKeyDown);
    this.player.removeEventListener('keyup', this.playerKeyUp);
    this.main.removeEventListener('keydown', this.hotKeyDown);
    this.main.removeEventListener('keydown', this.navigateTorchesKeyDown);
  }
}
